import {
  animate,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useMotionPreferences } from '../../motion/preferences';
import { ease } from '../../motion/tokens';
import { useAppMode } from '../../state/appMode';
import { introReducer, isIntroSkipKey, resolveInitialPhase, type IntroPhase } from './machine';
import { logoGroups } from './logoPaths';

export const INTRO_STORAGE_KEY = 'atc:intro-seen:v1';
let sessionIntroSeen = false;

interface OpeningSequenceProps {
  onComplete: () => void;
}

function readIntroSeen(): boolean {
  if (sessionIntroSeen) return true;
  try {
    return localStorage.getItem(INTRO_STORAGE_KEY) === '1';
  } catch {
    return sessionIntroSeen;
  }
}

function rememberIntro(): void {
  sessionIntroSeen = true;
  try {
    localStorage.setItem(INTRO_STORAGE_KEY, '1');
  } catch {
    // Session memory remains the non-blocking fallback.
  }
}

function initialPhase(reducedMotion: boolean): IntroPhase {
  return resolveInitialPhase({
    reducedMotion,
    introSeen: readIntroSeen(),
    hash: window.location.hash,
  });
}

function RevealPath({
  d,
  fill,
  stroke,
  delay,
  duration,
  instant,
}: {
  d: string;
  fill: string;
  stroke: string;
  delay: number;
  duration: number;
  instant: boolean;
}) {
  return (
    <g>
      <motion.path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        initial={instant ? { pathLength: 1, opacity: 0.16 } : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: instant ? 0.16 : [0, 0.58, 0.16] }}
        transition={{
          delay: instant ? 0 : delay,
          duration: instant ? 0.01 : duration,
          ease: ease.draw,
          times: instant ? undefined : [0, 0.84, 1],
        }}
      />
      {!instant && (
        <motion.path
          d={d}
          fill="none"
          stroke="#fff"
          strokeWidth="0.5"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 0.09, 0] }}
          transition={{
            delay: delay + 0.05,
            duration: duration * 0.92,
            ease: ease.draw,
            times: [0, 0.8, 1],
          }}
        />
      )}
      <motion.path
        d={d}
        fill={fill}
        fillRule="evenodd"
        initial={instant ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: instant ? 0 : delay + duration * 0.8,
          duration: instant ? 0.01 : duration * 0.58,
          ease: ease.entrance,
        }}
      />
    </g>
  );
}

export function OpeningSequence({ onComplete }: OpeningSequenceProps) {
  const { reducedMotion } = useMotionPreferences();
  const { durationScale } = useAppMode();
  const [phase, dispatch] = useReducer(introReducer, reducedMotion, initialPhase);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const dawnProgress = useMotionValue(0);
  const shellOpacity = useMotionValue(1);
  const waveY = useTransform(dawnProgress, [0, 1], ['72%', '-24%']);
  const logoWaveY = useTransform(dawnProgress, [0, 1], [2000, -1250]);
  const atmosphereOpacity = useTransform(dawnProgress, [0, 0.7, 1], [1, 0.25, 0]);
  const taglineOpacity = useTransform(dawnProgress, [0, 0.45, 0.72], [1, 0.85, 0]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const skip = useCallback(() => {
    if (phaseRef.current !== 'revealing') return;
    rememberIntro();
    dispatch('SKIP');
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isIntroSkipKey(event)) skip();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [skip]);

  useEffect(() => {
    if (phase === 'revealing') {
      const timer = window.setTimeout(() => {
        rememberIntro();
        dispatch('REVEAL_FINISHED');
      }, 4650 * durationScale);
      return () => window.clearTimeout(timer);
    }
    if (phase === 'holding') {
      const timer = window.setTimeout(() => dispatch('HOLD_FINISHED'), reducedMotion ? 10 : 400 * durationScale);
      return () => window.clearTimeout(timer);
    }
    if (phase === 'dawn') {
      const duration = reducedMotion ? 0.2 : 0.9 * durationScale;
      const controls = reducedMotion
        ? animate(shellOpacity, 0, { duration, ease: ease.exit })
        : animate(dawnProgress, 1, { duration, ease: ease.entrance });
      if (reducedMotion) dawnProgress.set(1);
      const timer = window.setTimeout(() => dispatch('DAWN_FINISHED'), duration * 1000);
      return () => {
        controls.stop();
        window.clearTimeout(timer);
      };
    }
    onComplete();
  }, [dawnProgress, durationScale, onComplete, phase, reducedMotion, shellOpacity]);

  const instant = phase !== 'revealing';

  return (
    <motion.section
      className="fixed inset-0 z-50 overflow-hidden bg-atc-dark text-white"
      aria-label="ATC インタラクティブレポート オープニング"
      onPointerDown={(event) => {
        if ((event.target as HTMLElement).closest('button')) return;
        skip();
      }}
      style={{ opacity: shellOpacity }}
    >
      <motion.div
        className="absolute inset-0"
        style={{ opacity: atmosphereOpacity }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 42%, rgba(133,102,169,.12), transparent 0 18%), radial-gradient(circle at 12% 44%, rgba(243,240,0,.08), transparent 0 12%), linear-gradient(180deg,#08090b 0%,#0c0e12 38%,#11141a 100%)',
        }}
        />
      </motion.div>
      <button
        type="button"
        onClick={skip}
        className="atc-dark-material absolute right-5 top-5 z-30 rounded-full border border-white/30 bg-black/55 px-5 py-3 text-sm font-medium tracking-wide text-white shadow-lg backdrop-blur-xl transition-transform duration-100 active:scale-[.97] md:right-8 md:top-8"
      >
        スキップ
      </button>

      <div className="relative z-10 flex min-h-svh items-center justify-center p-6 md:p-12">
        <motion.svg
          viewBox="0 0 2048 1870"
          className="h-auto max-h-[46svh] w-[min(78vw,56rem)]"
          role="img"
          aria-labelledby="atc-logo-title atc-logo-desc"
          initial={instant ? false : { opacity: 0, scale: 0.992 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: instant ? 0.01 : 1.4, ease: ease.entrance }}
        >
          <title id="atc-logo-title">ATC Asia and Pacific Trade Center</title>
          <desc id="atc-logo-desc">ATCロゴが線からゆっくりと現れ、朝の光とともに青へ変わる。</desc>
          <defs>
            <filter id="atc-soft-shadow" x="-12%" y="-12%" width="124%" height="124%">
              <feDropShadow dx="0" dy="10" stdDeviation="18" floodColor="#000" floodOpacity=".32" />
            </filter>
            <clipPath id="atc-dawn-logo-clip">
              <motion.path
                d="M-240 450 C280 120 650 640 1080 310 C1440 35 1760 340 2290 20 L2290 2200 L-240 2200 Z"
                style={{ y: logoWaveY }}
              />
            </clipPath>
          </defs>
          <g filter="url(#atc-soft-shadow)">
            {logoGroups.map((group) => (
              <g key={group.key}>
                {group.paths.map((d, index) => (
                  <RevealPath
                    key={`${group.key}-${index}`}
                    d={d}
                    fill="#ffffff"
                    stroke={group.stroke}
                    delay={(group.delay + index * 0.05) * durationScale}
                    duration={group.duration * durationScale}
                    instant={instant}
                  />
                ))}
              </g>
            ))}
            <motion.text
              x="1058"
              y="1360"
              textAnchor="middle"
              fill="#a999bf"
              fontSize="70"
              fontWeight="400"
              letterSpacing="1.4"
              style={{ opacity: taglineOpacity, fontFamily: '"Noto Sans JP", sans-serif' }}
              initial={instant ? { opacity: 1 } : { opacity: 0, translateY: 6 }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ delay: instant ? 0 : 3.4 * durationScale, duration: instant ? 0.01 : 1.25 * durationScale, ease: ease.entrance }}
            >
              ASIA AND PACIFIC TRADE CENTER
            </motion.text>
            <g clipPath="url(#atc-dawn-logo-clip)" aria-hidden="true">
              {logoGroups.flatMap((group) =>
                group.paths.map((d, index) => (
                  <path
                    key={`dawn-${group.key}-${index}`}
                    d={d}
                    fill="#1a60a2"
                    fillRule="evenodd"
                  />
                )),
              )}
            </g>
          </g>
        </motion.svg>
      </div>

      <motion.svg
        className="pointer-events-none absolute inset-x-[-10%] bottom-[-38%] z-20 h-[125%] w-[120%]"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        style={{ y: waveY }}
        aria-hidden="true"
      >
        <path
          d="M0 250 C260 90 480 360 750 205 C1010 56 1180 160 1440 40 L1440 900 L0 900 Z"
          fill="#fafbff"
        />
      </motion.svg>

      <span className="sr-only" aria-live="polite">
        {phase === 'revealing' ? 'ロゴを表示しています。スキップできます。' : phase === 'dawn' ? 'レポートを開いています。' : ''}
      </span>
    </motion.section>
  );
}
